/**
 * Frontend Logger Abstraction
 *
 * Provides a unified logging interface for the frontend application.
 * Logs are redacted before being forwarded to any external sink.
 */

import { createSafeContext, redactSensitiveFields } from './redaction';

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

export type LogEntry = {
  level: LogLevel;
  message: string;
  context?: Record<string, unknown>;
  timestamp: string;
};

export type LogSink = (entry: LogEntry) => void;

/**
 * Log sinks that receive formatted log entries.
 * External sinks (like Datadog) are registered separately.
 */
const sinks: LogSink[] = [];

/**
 * Registers a log sink to receive all log entries.
 */
export const registerLogSink = (sink: LogSink): void => {
  sinks.push(sink);
};

/**
 * Removes a registered log sink.
 */
export const removeLogSink = (sink: LogSink): void => {
  const index = sinks.indexOf(sink);
  if (index > -1) {
    sinks.splice(index, 1);
  }
};

/**
 * Clears all registered log sinks (useful for testing).
 */
export const clearLogSinks = (): void => {
  sinks.length = 0;
};

/**
 * Creates a log entry with redacted context.
 */
const createLogEntry = (
  level: LogLevel,
  message: string,
  context?: Record<string, unknown>
): LogEntry => {
  return {
    level,
    message: typeof message === 'string' ? message : String(message),
    context: context ? createSafeContext(context) : undefined,
    timestamp: new Date().toISOString(),
  };
};

/**
 * Dispatches a log entry to all registered sinks.
 */
const dispatch = (entry: LogEntry): void => {
  for (const sink of sinks) {
    try {
      sink(entry);
    } catch {
      // Silently ignore sink errors to prevent cascading failures
    }
  }
};

/**
 * Frontend logger with automatic redaction.
 */
export const logger = {
  debug: (message: string, context?: Record<string, unknown>): void => {
    const entry = createLogEntry('debug', message, context);
    dispatch(entry);
  },

  info: (message: string, context?: Record<string, unknown>): void => {
    const entry = createLogEntry('info', message, context);
    dispatch(entry);
  },

  warn: (message: string, context?: Record<string, unknown>): void => {
    const entry = createLogEntry('warn', message, context);
    dispatch(entry);
  },

  error: (message: string, context?: Record<string, unknown>): void => {
    const entry = createLogEntry('error', message, context);
    dispatch(entry);
  },
};

/**
 * Serializes an Error object into a plain object preserving
 * non-enumerable properties (name, message, stack).
 */
export const serializeError = (error: Error): Record<string, unknown> => {
  const serialized: Record<string, unknown> = {
    name: error.name,
    message: error.message,
    stack: error.stack,
  };

  const customProps = Object.entries(error);
  if (customProps.length > 0) {
    const redacted = redactSensitiveFields(Object.fromEntries(customProps));
    Object.assign(serialized, redacted);
  }

  return serialized;
};

/**
 * Formats arguments into a context object, redacting sensitive data.
 * Used by console capture to convert arbitrary console arguments.
 * Properly serializes Error objects to preserve stack traces.
 */
export const formatArgsAsContext = (
  args: unknown[]
): Record<string, unknown> | undefined => {
  if (args.length === 0) {
    return undefined;
  }

  if (args.length === 1 && typeof args[0] === 'object' && args[0] !== null) {
    if (args[0] instanceof Error) {
      return serializeError(args[0]);
    }
    return redactSensitiveFields(args[0] as Record<string, unknown>);
  }

  const context: Record<string, unknown> = {};
  args.forEach((arg, index) => {
    if (arg instanceof Error) {
      context[`arg${index}`] = serializeError(arg);
    } else if (typeof arg === 'object' && arg !== null) {
      context[`arg${index}`] = redactSensitiveFields(arg);
    } else {
      context[`arg${index}`] = arg;
    }
  });

  return context;
};
